import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Check, Settings, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'bcool_cookie_consent_v1';

const defaultConsent = {
  necessary: true,   // toujours activé, non modifiable
  analytics: false,
  advertising: false,
  timestamp: null,
};

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

export function saveConsent(consent) {
  const withTs = { ...consent, timestamp: new Date().toISOString() };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(withTs)); } catch (e) { /* ignore */ }
  window.dispatchEvent(new CustomEvent('bcool:consent-updated', { detail: withTs }));
}

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [state, setState] = useState(defaultConsent);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) setVisible(true);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, advertising: true });
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, advertising: false });
    setVisible(false);
  };

  const savePreferences = () => {
    saveConsent(state);
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 md:p-5 pointer-events-none">
      <div className="max-w-4xl mx-auto bg-white border-2 border-[#1a1414] shadow-2xl pointer-events-auto">
        {!showDetails ? (
          // Vue compacte
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#c9a961]/20 flex items-center justify-center flex-shrink-0">
                <Cookie size={18} className="text-[#8b2c3f]" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-[#1a1414] mb-1">
                  Un instant, avant de continuer votre lecture
                </h3>
                <p className="text-[13px] text-[#3a3232] leading-relaxed">
                  Nous utilisons des cookies pour mesurer l&apos;audience, afficher des
                  publicités et améliorer votre expérience. Vous pouvez tout accepter,
                  tout refuser, ou choisir précisément.{' '}
                  <Link to="/cookies" className="underline text-[#8b2c3f] hover:text-[#6d1f2f]">
                    En savoir plus
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={acceptAll}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white text-[12px] uppercase font-semibold tracking-[0.16em] px-5 py-2.5 transition-colors"
              >
                <Check size={14} /> Tout accepter
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1a1414] hover:bg-[#1a1414] hover:text-white text-[#1a1414] text-[12px] uppercase font-semibold tracking-[0.16em] px-5 py-2.5 transition-colors"
              >
                <X size={14} /> Tout refuser
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 text-[#3a3232] hover:text-[#8b2c3f] text-[12px] uppercase font-semibold tracking-[0.16em] px-5 py-2.5 transition-colors"
              >
                <Settings size={14} /> Personnaliser
              </button>
            </div>
          </div>
        ) : (
          // Vue détaillée
          <div className="p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-bold text-[#1a1414]">Vos préférences de cookies</h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-black">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <Category
                title="Cookies essentiels"
                desc="Nécessaires au fonctionnement du site (connexion, panier, préférences). Toujours activés."
                checked
                disabled
              />
              <Category
                title="Mesure d'audience"
                desc="Nous permet de comprendre comment vous utilisez le site (pages vues, temps passé). Aucune donnée personnelle vendue."
                checked={state.analytics}
                onChange={(v) => setState((s) => ({ ...s, analytics: v }))}
              />
              <Category
                title="Publicité personnalisée"
                desc="Nous permet d'afficher des publicités adaptées à vos centres d'intérêt. Sans ce consentement, vous verrez des publicités génériques."
                checked={state.advertising}
                onChange={(v) => setState((s) => ({ ...s, advertising: v }))}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-4 border-t border-gray-100">
              <button
                onClick={savePreferences}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white text-[12px] uppercase font-semibold tracking-[0.16em] px-5 py-2.5"
              >
                Enregistrer mes choix <ChevronRight size={14} />
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 border border-[#1a1414] hover:bg-[#1a1414] hover:text-white text-[#1a1414] text-[12px] uppercase font-semibold tracking-[0.16em] px-5 py-2.5"
              >
                Tout accepter
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Category = ({ title, desc, checked, onChange, disabled = false }) => (
  <div className="border border-gray-200 p-4 flex items-start justify-between gap-4">
    <div className="min-w-0">
      <h4 className="text-[13px] font-bold text-[#1a1414]">{title}</h4>
      <p className="text-[12px] text-[#7a716c] mt-1 leading-relaxed">{desc}</p>
    </div>
    <label className={`inline-flex items-center gap-2 flex-shrink-0 ${disabled ? 'opacity-50' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="w-4 h-4 accent-[#8b2c3f]"
      />
      <span className="text-[11px] uppercase text-[#3a3232] font-semibold">
        {checked ? 'Activé' : 'Désactivé'}
      </span>
    </label>
  </div>
);

export default CookieBanner;
